import React, { useEffect, useState } from "react";
import { UserCirclePlus, X, UsersThree, Lock, Envelope, ShieldCheck } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { GlassCard } from "@/components/glass/GlassCard";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Per-contract ACL management card.
 * Only rendered to admins / contract_managers / global staff.
 * Allows granting or revoking signer / viewer access by email.
 */
export default function ContractACLCard({ contractId, ownerId, ownerEmail }) {
  const { user } = useAuth();
  const [acl, setAcl] = useState({});
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const canManage = user?.is_global || ["admin", "contract_manager"].includes(user?.role);

  const load = () => {
    api.get(`/contracts/${contractId}/acl`).then(r => setAcl(r.data.acl || {})).catch(() => {});
  };
  useEffect(() => { if (contractId) load(); }, [contractId]);

  if (!canManage) return null;

  const grant = async (kind) => {
    if (!email || !email.includes("@")) { toast.error("Enter a valid email"); return; }
    setLoading(true);
    try {
      const payload = kind === "signer" ? { signer_emails: [email] } : { viewer_emails: [email] };
      const { data } = await api.post(`/contracts/${contractId}/acl`, payload);
      setAcl(data.acl || {});
      setEmail("");
      toast.success(`${email} granted ${kind} access`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  const revoke = async (em) => {
    setLoading(true);
    try {
      const { data } = await api.delete(`/contracts/${contractId}/acl`, { params: { email: em } });
      setAcl(data.acl || {});
      toast.success(`${em} access revoked`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  const signerEmails = acl?.signer_emails || [];
  const viewerEmails = acl?.viewer_emails || [];

  return (
    <GlassCard className="p-5" data-testid="acl-card">
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck size={16} weight="fill" className="text-[var(--accent)]" />
        <div className="font-medium">Access control</div>
        <Badge variant="outline" className="ml-auto text-[10px]" data-testid="acl-grantee-count">
          {signerEmails.length + viewerEmails.length} invited
        </Badge>
      </div>
      <p className="text-xs text-[var(--text-3)] mb-4">
        Signatories &amp; viewers only see contracts they've been granted access to.
        Owner (<code className="px-1 rounded bg-[var(--surface-2)]">{ownerEmail || ownerId}</code>) always has full access.
      </p>

      <div className="flex gap-2 mb-4">
        <Input
          type="email"
          placeholder="email@company.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="acl-email-input"
          className="flex-1"
        />
        <Button onClick={() => grant("signer")} disabled={loading} size="sm" data-testid="acl-grant-signer-btn" className="gap-1.5">
          <UserCirclePlus size={14} /> Add signer
        </Button>
        <Button onClick={() => grant("viewer")} disabled={loading} size="sm" variant="outline" data-testid="acl-grant-viewer-btn" className="gap-1.5">
          <Eye /> Add viewer
        </Button>
      </div>

      {signerEmails.length + viewerEmails.length === 0 ? (
        <div className="text-xs text-[var(--text-3)] italic py-3" data-testid="acl-empty">
          No one has been granted per-contract access yet.
          Tenant admins &amp; contract managers see this contract regardless.
        </div>
      ) : (
        <div className="space-y-2">
          {signerEmails.map(em => (
            <AccessRow key={`s-${em}`} email={em} kind="signer" onRevoke={() => revoke(em)} testId={`acl-signer-${em}`} />
          ))}
          {viewerEmails.map(em => (
            <AccessRow key={`v-${em}`} email={em} kind="viewer" onRevoke={() => revoke(em)} testId={`acl-viewer-${em}`} />
          ))}
        </div>
      )}
    </GlassCard>
  );
}

function Eye(props) {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>;
}

function AccessRow({ email, kind, onRevoke, testId }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-[var(--surface-2)]/60 border border-[var(--border)]" data-testid={testId}>
      {kind === "signer" ? <Envelope size={13} weight="fill" /> : <UsersThree size={13} />}
      <span className="text-sm flex-1 truncate">{email}</span>
      <Badge variant="secondary" className="text-[9px]">{kind}</Badge>
      <button onClick={onRevoke} aria-label="Revoke access" className="text-[var(--text-3)] hover:text-[var(--danger)] p-1" data-testid={`${testId}-revoke`}>
        <X size={12} />
      </button>
    </div>
  );
}
